import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { User } from "lucide-react";
import "./Customers.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditCustomer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [houseTypes, setHouseTypes] = useState([]);
  const [zones, setZones] = useState([]);
  const [collectors, setCollectors] = useState([]);
  
  const [formData, setFormData] = useState({
    customerName: "",
    phone: "",
    houseNo: "",
    rate: "",
    note: "",
    houseType: "",
    zone: "",
    collector: "",
    isOwner: false,
  });

  // Fetch customer data
  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        console.log("📤 Fetching customer:", id);
        const res = await axios.get(`${url}/api/customer/${id}`);
        const customer = res.data.data;
        
        console.log("📥 Customer data:", customer);

        setFormData({
          customerName: customer.customerName || "",
          phone: customer.phone || "",
          houseNo: customer.houseNo || "",
          rate: customer.rate || "",
          note: customer.note || "",
          houseType: customer.houseType?._id || "",
          zone: customer.zone?._id || "",
          collector: customer.collector?._id || "",
          isOwner: customer.isOwner || false,
        });

        setLoading(false);
      } catch (err) {
        console.error("❌ Failed to load customer:", err);
        toast.error(err.response?.data?.message || "Failed to load customer");
        navigate("/customers");
      }
    };
    
    if (id) {
      fetchCustomer();
    }
  }, [id, navigate]);

  // Fetch options for dropdowns
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        console.log("📤 Fetching options...");
        const [htRes, zRes, colRes] = await Promise.all([
          axios.get(`${url}/api/housetype`),
          axios.get(`${url}/api/zone`),
          axios.get(`${url}/api/collectors`),
        ]);
        
        setHouseTypes(htRes.data.data || []);
        setZones(zRes.data.data || []);
        setCollectors(colRes.data.data || []);
        
        console.log("📥 Options loaded:", {
          houseTypes: htRes.data.data?.length,
          zones: zRes.data.data?.length,
          collectors: colRes.data.data?.length
        });
      } catch (err) {
        console.error("❌ Failed to load options:", err);
        toast.error("Failed to load form options");
      }
    };
    fetchOptions();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Validate required fields
    if (!formData.customerName || !formData.houseNo || !formData.rate || !formData.houseType || !formData.zone || !formData.collector) {
      toast.error("Please fill all required fields");
      setSubmitting(false);
      return;
    }

    try {
      console.log("📤 Updating customer with data:", formData);
      
      const res = await axios.put(`${url}/api/customer/${id}`, formData);
      
      console.log("📥 Update response:", res.data);
      toast.success("Customer updated successfully!");
      navigate("/customers");
    } catch (err) {
      console.error("❌ Update failed:", err);
      console.error("Error response:", err.response?.data);
      toast.error(err.response?.data?.message || "Update failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="customers-container">
        <div className="loading">Loading customer data...</div>
      </div>
    );
  }

  return (
    <div className="customers-container">
      <div className="header-section">
        <h1 className="page-title">
          <User size={24} className="title-icon" /> Edit Customer
        </h1>
        <button 
          onClick={() => navigate("/customers")} 
          className="export-btn"
        >
          Back to List
        </button>
      </div>

      <div className="filters-section" style={{ display: 'block' }}>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '20px' }}>
            {/* Customer Name */}
            <div className="form-group">
              <label>Customer Name *</label>
              <input
                type="text"
                name="customerName"
                value={formData.customerName}
                onChange={handleChange}
                placeholder="Enter customer name"
                required
                className="filter-input"
              />
            </div>

            {/* Phone */}
            <div className="form-group">
              <label>Phone</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter phone number"
                className="filter-input"
              />
            </div>

            {/* House Number */}
            <div className="form-group">
              <label>House No *</label>
              <input
                type="number"
                name="houseNo"
                value={formData.houseNo}
                onChange={handleChange}
                placeholder="Enter house number"
                required
                className="filter-input"
              />
            </div>

            {/* Rate */}
            <div className="form-group">
              <label>Rate ($) *</label>
              <input
                type="number"
                name="rate"
                value={formData.rate}
                onChange={handleChange}
                placeholder="Enter rate"
                min="0"
                step="0.01"
                required
                className="filter-input"
              />
            </div>

            {/* House Type Dropdown */}
            <div className="form-group">
              <label>House Type *</label>
              <select
                name="houseType"
                value={formData.houseType}
                onChange={handleChange}
                required
                className="filter-select"
              >
                <option value="">Select House Type</option>
                {houseTypes.map((h) => (
                  <option key={h._id} value={h._id}>
                    {h.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Zone Dropdown */}
            <div className="form-group">
              <label>Zone *</label>
              <select
                name="zone"
                value={formData.zone}
                onChange={handleChange}
                required
                className="filter-select"
              >
                <option value="">Select Zone</option>
                {zones.map((z) => (
                  <option key={z._id} value={z._id}>
                    {z.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Collector Dropdown */}
            <div className="form-group">
              <label>Collector *</label>
              <select
                name="collector"
                value={formData.collector}
                onChange={handleChange}
                required
                className="filter-select"
              >
                <option value="">Select Collector</option>
                {collectors.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.fname} {c.lname} ({c.username})
                  </option>
                ))}
              </select>
            </div>

            {/* Is Owner Checkbox */}
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginTop: '25px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  name="isOwner"
                  checked={formData.isOwner}
                  onChange={handleChange}
                  style={{ width: '18px', height: '18px' }}
                />
                <span>Is Owner?</span>
              </label>
            </div>

            {/* Note - Full Width */}
            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label>Note</label>
              <textarea
                name="note"
                value={formData.note || ""}
                onChange={handleChange}
                placeholder="Enter any notes"
                rows="3"
                className="filter-input"
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>

          <div className="form-actions" style={{ marginTop: '30px', display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="summary-btn"
              onClick={() => navigate("/customers")}
              style={{ backgroundColor: '#f44336' }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="export-btn"
              disabled={submitting}
            >
              {submitting ? "Updating..." : "Update Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditCustomer;